import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Warning, Trash, ShieldWarning, X, ArrowLeft } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Two-step destructive account deletion flow:
 *   Step 1: Detailed warning + mandatory awareness checkboxes.
 *   Step 2: Type-to-confirm phrase + password re-verification (email users).
 *
 * Prevents accidental deletion with multiple gates; mirrors the mobile app UX.
 */
export const DeleteAccountDialog = ({ open, onClose }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [info, setInfo] = useState(null);

  // Step 1 state
  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [ack3, setAck3] = useState(false);

  // Step 2 state
  const [confirmationPhrase, setConfirmationPhrase] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep(1);
    setAck1(false); setAck2(false); setAck3(false);
    setConfirmationPhrase(''); setPassword(''); setReason('');
    setSubmitting(false);
  };

  useEffect(() => {
    if (!open) return;
    reset();
    api.get('/auth/delete-account/info')
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ confirmation_phrase: 'ELIMINAR', password_required: true }));
  }, [open]);

  if (!open) return null;

  const allAck = ack1 && ack2 && ack3;
  const phraseOk = info && confirmationPhrase.trim().toUpperCase() === info.confirmation_phrase;
  const passwordOk = !info?.password_required || password.length >= 6;
  const canSubmit = phraseOk && passwordOk && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.delete('/auth/delete-account', {
        data: {
          confirmation_phrase: confirmationPhrase.trim().toUpperCase(),
          password: info?.password_required ? password : undefined,
          reason: reason.trim() || undefined,
        },
      });
      toast.success('Tu cuenta fue eliminada. Lamentamos verte partir.');
      try { await logout(); } catch (_) { /* local state cleared anyway */ }
      setTimeout(() => { navigate('/', { replace: true }); window.location.reload(); }, 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar la cuenta. Intenta de nuevo.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-zinc-950/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="delete-account-dialog"
    >
      <div className="w-full max-w-xl bg-white dark:bg-zinc-900 border-2 border-red-500/60 shadow-2xl my-8">
        {/* Header (red - always visible) */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldWarning className="w-7 h-7 text-white flex-shrink-0" weight="fill" />
            <div>
              <h2 className="text-lg font-bold text-white">Eliminar cuenta — Acción permanente</h2>
              <p className="text-sm text-red-100">Paso {step} de 2</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white"
            data-testid="delete-account-close"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {step === 1 && (
          <>
            <div className="p-6 space-y-5">
              {/* Big warning banner */}
              <div className="border-2 border-red-400 bg-red-50 dark:bg-red-950/30 dark:border-red-600 p-4 flex gap-3">
                <Warning className="w-8 h-8 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" weight="fill" />
                <div className="space-y-2">
                  <p className="font-bold text-red-800 dark:text-red-300 text-sm uppercase tracking-wide">
                    Esta acción es irreversible
                  </p>
                  <p className="text-sm text-red-900 dark:text-red-200 leading-relaxed">
                    Una vez eliminada, tu cuenta no podrá recuperarse. Perderás de forma permanente
                    todo acceso a MaterMatch y a tu historial dentro de la plataforma.
                  </p>
                </div>
              </div>

              {/* What gets deleted */}
              <div>
                <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-100 mb-2 uppercase tracking-wide">
                  Lo que se eliminará permanentemente
                </h3>
                <ul className="space-y-1.5 text-sm text-zinc-700 dark:text-zinc-300 list-disc list-inside pl-1">
                  <li>Tu perfil, nombre, correo, teléfono y foto.</li>
                  <li>Tus datos fiscales (Constancia de Situación Fiscal, RFC, domicilio).</li>
                  <li>Tus borradores de cotización y propuestas sin enviar.</li>
                  <li>Tus preferencias de notificaciones, alertas de precio y suscripciones.</li>
                  <li>Todas tus sesiones activas en cualquier dispositivo.</li>
                </ul>
              </div>

              {/* What is retained */}
              <div>
                <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-100 mb-2 uppercase tracking-wide">
                  Lo que se conserva por obligación fiscal
                </h3>
                <ul className="space-y-1.5 text-sm text-zinc-700 dark:text-zinc-300 list-disc list-inside pl-1">
                  <li>Transacciones completadas, folios de contrato y comisiones pagadas.</li>
                  <li>Facturas CFDI emitidas por MaterMatch (retención 5 años, CFF Art. 30).</li>
                  <li>Calificaciones emitidas o recibidas (se anonimizan).</li>
                </ul>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 italic mt-2">
                  Estos registros se anonimizan y se conservan únicamente con fines de cumplimiento legal.
                </p>
              </div>

              {/* Awareness checkboxes */}
              <div className="border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 p-4 space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
                  Confirma que entiendes:
                </p>
                {[
                  { state: ack1, setState: setAck1, label: 'Entiendo que esta acción no se puede deshacer.', testid: 'delete-ack-1' },
                  { state: ack2, setState: setAck2, label: 'Entiendo que perderé el acceso a todos mis proyectos, cotizaciones y conversaciones.', testid: 'delete-ack-2' },
                  { state: ack3, setState: setAck3, label: 'Entiendo que si quiero regresar a MaterMatch deberé registrarme de nuevo desde cero.', testid: 'delete-ack-3' },
                ].map((item) => (
                  <label key={item.testid} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={item.state}
                      onChange={(e) => item.setState(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-red-600 flex-shrink-0"
                      data-testid={item.testid}
                    />
                    <span className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug">{item.label}</span>
                  </label>
                ))}
              </div>

              <p className="text-center text-xs text-zinc-500 dark:text-zinc-400">
                ¿Tienes una duda antes de continuar? Escríbenos a{' '}
                <a href="mailto:contacto@matermatch.com" className="underline hover:text-orange-600">contacto@matermatch.com</a>
              </p>
            </div>

            <div className="px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-t border-zinc-200 dark:border-zinc-800 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                className="rounded-none"
                data-testid="delete-cancel-step-1"
              >
                Cancelar — mantener mi cuenta
              </Button>
              <Button
                type="button"
                onClick={() => setStep(2)}
                disabled={!allAck}
                className="rounded-none bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed h-11 px-6"
                data-testid="delete-continue-step-1"
              >
                Entiendo, continuar
              </Button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="p-6 space-y-5">
              <div className="border-l-4 border-red-600 bg-red-50 dark:bg-red-950/30 p-4">
                <p className="text-sm font-semibold text-red-800 dark:text-red-300 leading-relaxed">
                  Último paso. Para confirmar la eliminación completa de tu cuenta
                  {info?.email && <> <span className="font-mono">({info.email})</span></>}:
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                  Escribe <span className="font-mono bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 px-2 py-0.5">{info?.confirmation_phrase || 'ELIMINAR'}</span> para confirmar
                </label>
                <Input
                  type="text"
                  value={confirmationPhrase}
                  onChange={(e) => setConfirmationPhrase(e.target.value)}
                  placeholder={info?.confirmation_phrase || 'ELIMINAR'}
                  autoComplete="off"
                  className="rounded-none h-11 font-mono uppercase tracking-widest border-zinc-300"
                  data-testid="delete-confirmation-input"
                />
                {confirmationPhrase && !phraseOk && (
                  <p className="text-xs text-red-600 italic">
                    Debes escribir exactamente: <span className="font-mono">{info?.confirmation_phrase || 'ELIMINAR'}</span>
                  </p>
                )}
              </div>

              {info?.password_required && (
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                    Contraseña actual <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Tu contraseña"
                    autoComplete="current-password"
                    className="rounded-none h-11 border-zinc-300"
                    data-testid="delete-password-input"
                  />
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    Para tu seguridad, necesitamos confirmar tu identidad.
                  </p>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                  ¿Por qué te vas? <span className="text-zinc-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, 500))}
                  placeholder="Tu retroalimentación nos ayuda a mejorar"
                  rows={3}
                  className="w-full rounded-none border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-3 text-sm resize-none focus:outline-none focus:border-orange-500"
                  data-testid="delete-reason-input"
                />
                <p className="text-xs text-zinc-400 text-right">{reason.length}/500</p>
              </div>
            </div>

            <div className="px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-t border-zinc-200 dark:border-zinc-800 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep(1)}
                  disabled={submitting}
                  className="rounded-none"
                  data-testid="delete-back-step-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Atrás
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  disabled={submitting}
                  className="rounded-none"
                  data-testid="delete-cancel-step-2"
                >
                  Cancelar
                </Button>
              </div>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="rounded-none bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed h-11 px-6"
                data-testid="delete-submit-btn"
              >
                <Trash className="w-4 h-4 mr-2" weight="bold" />
                {submitting ? 'Eliminando…' : 'Eliminar mi cuenta permanentemente'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteAccountDialog;
